import { FC, useId } from "react";

import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import { FormType, AvailablePaymentCurrency } from "@simplyk/common";
import { DateTime } from "luxon";
import { UseFormReturn, useFieldArray } from "react-hook-form";

import { AuctionDetailDrawerLayout } from "./AuctionDetailDrawerLayout";
import { resolveAuctionDrawerBidState } from "./bid-gate.helper";
import { getRateExtensionInfo } from "./bid-status.helper";
import { BidHistoryList } from "./BidHistoryList/BidHistoryList";
import { DrawerImageDisplay } from "./DrawerImageDisplay/DrawerImageDisplay";
import { DrawerTitle } from "./DrawerTitle";
import { FormBidBlock } from "./FormBidBlock/FormBidBlock";
import { useAuctionCountdown } from "./useAuctionCountdown";
import { useAuctionDetailDrawer } from "./useAuctionDetailDrawer";
import { useDrawerBidState } from "./useDrawerBidState";

import { useSalesOpenSchedule } from "@/canvas/features/FormV2/blocks/campaign-details/SalesOpeningChip/useSalesOpenSchedule";
import { QuestionField } from "@/canvas/features/FormV2/blocks/questions/QuestionField/QuestionField";
import { AuctionBidBadgeChip } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/AuctionRateCard/AuctionBidBadgeChip";
import { RateCardDescription } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/RateCardDescription";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { initAnswer } from "@/canvas/features/FormV2/contexts/TicketingV2Provider";
import { isRateClosed } from "@/canvas/features/FormV2/helpers/useIsRateClosed";
import { useFrontendAuctionContext } from "@/common/contexts/FrontendAuctionContext";
import { shouldDisplayAuctionRateTaxMention } from "@/common/helpers/rates";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ParticipantAnswers, TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput, RateOutput } from "@/common/types/trpc";
import { FormDrawer, FormDrawerProps } from "@/forms-design-system/FormDrawer/FormDrawer";
import { FormInfobox } from "@/forms-design-system/FormInfobox";
import { Lock, Time } from "@/icons/outlined";

export interface AuctionDetailDrawerProps extends Pick<
  FormDrawerProps,
  "onNext" | "onPrevious" | "hasNext" | "hasPrevious"
> {
  open: boolean;
  onClose: () => void;
  onGoToCheckout: () => void;
  rate: RateOutput;
  formCurrency: AvailablePaymentCurrency;
  form: UseFormReturn<TicketingPaymentInput>;
  questions: QuestionOutput[];
  auctionEndDateUtc?: string;
}

export const AuctionDetailDrawer: FC<AuctionDetailDrawerProps> = ({
  open,
  onClose,
  onGoToCheckout,
  rate,
  formCurrency,
  form,
  questions,
  auctionEndDateUtc,
  ...navigationProps
}) => {
  const titleId = useId();

  return (
    <FormDrawer open={open} onClose={onClose} size="medium" aria-labelledby={titleId} {...navigationProps}>
      <Fade in key={rate.id} timeout={250}>
        <div>
          <AuctionDetailDrawerContent
            key={rate.id}
            titleId={titleId}
            open={open}
            rate={rate}
            onClose={onClose}
            onGoToCheckout={onGoToCheckout}
            formCurrency={formCurrency}
            form={form}
            questions={questions}
            auctionEndDateUtc={auctionEndDateUtc}
          />
        </div>
      </Fade>
    </FormDrawer>
  );
};

interface AuctionDetailDrawerContentProps {
  titleId: string;
  open: boolean;
  rate: RateOutput;
  onClose: () => void;
  onGoToCheckout: () => void;
  formCurrency: AvailablePaymentCurrency;
  form: UseFormReturn<TicketingPaymentInput>;
  questions: QuestionOutput[];
  auctionEndDateUtc?: string;
}

const AuctionDetailDrawerContent: FC<AuctionDetailDrawerContentProps> = ({
  titleId,
  open,
  rate,
  onClose,
  onGoToCheckout,
  formCurrency,
  form,
  questions,
  auctionEndDateUtc,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { formObject: ticketing, isTicketingClosed: ticketingIsClosed } = useFormV2Context(FormType.Ticketing);
  const { highestBidsMapByRateId, commandBidsRateIdsMap, hasAlreadyBid, bidderCommandIds } =
    useFrontendAuctionContext();
  const salesSchedule = useSalesOpenSchedule();
  const rateAntiSnipingData = highestBidsMapByRateId?.[rate.id];
  const { isExtended: isRateExtended, effectiveEndUtc: extendedEndUtc } = getRateExtensionInfo(rateAntiSnipingData);
  const countdownEndDate = extendedEndUtc ?? auctionEndDateUtc;
  const rateClosed = isRateClosed(countdownEndDate);
  const { label: countdownLabel } = useAuctionCountdown(countdownEndDate, { extended: isRateExtended });
  const bidState = resolveAuctionDrawerBidState({ ticketingIsClosed, isRateClosed: rateClosed, salesSchedule });
  const isBiddingBlocked = bidState.kind !== "open";

  const drawerData = useAuctionDetailDrawer({
    rate,
    formCurrency,
    ticketingId: ticketing.id,
    rateData: rateAntiSnipingData,
    commandBidsRateIdsMap,
    hasAlreadyBid,
    isDrawerOpen: open,
    currentUserCommandIds: bidderCommandIds,
  });

  const {
    bidAmountInCents,
    bidMode,
    submitted,
    isEditingBid,
    inputError,
    isAlreadyOutbidWhileEditing,
    handleSubmit,
    handleBidAmountChange,
    handleBidModeChange,
    handleEditBid,
    handleCancelBid,
  } = useDrawerBidState({
    rate,
    form,
    rateData: rateAntiSnipingData,
    currentUserCommandIds: bidderCommandIds,
    minimumBidAmountInCents: drawerData.minimumBidAmountInCents,
    formCurrency,
    onClose,
    isBiddingBlocked,
  });

  const { update } = useFieldArray({
    control: form.control,
    name: "customAnswersOfParticipants",
  });

  const rateQuestions = questions.filter((question) => question.rateQuestions?.some((rq) => rq.rateId === rate.id));
  const hasBidOnThisRate = Boolean(commandBidsRateIdsMap?.[rate.id]);

  const isAlreadySeeded = Boolean(form.getValues(`customAnswersOfParticipants.${rate.sortIndex}`)?.rateId);
  if (!isAlreadySeeded && rateQuestions.length > 0 && !hasBidOnThisRate) {
    const newCustomAnswers: ParticipantAnswers = {
      rateId: rate.id,
      answers: rateQuestions.map(initAnswer),
    };
    update(rate.sortIndex, newCustomAnswers);
  }

  const zonedSalesOpenDate =
    bidState.kind === "salesScheduled"
      ? DateTime.fromJSDate(bidState.salesOpenDate)
          .setZone(ticketing.eventTimezone ?? undefined)
          .setLocale(locale)
      : null;

  const hasImages = drawerData.photoUrls.length > 0;
  const hasBidAmount = bidAmountInCents !== null && bidAmountInCents > 0;
  const questionsNode =
    rateQuestions.length > 0 && !hasBidOnThisRate && hasBidAmount ? (
      <Stack gap={1.5}>
        {rateQuestions.map((question, answerIndex) => (
          <QuestionField
            key={question.id}
            name={`customAnswersOfParticipants.${rate.sortIndex}.answers.${answerIndex}`}
            question={question}
            id={`drawer-rate-${rate.sortIndex}-question-${answerIndex}`}
          />
        ))}
      </Stack>
    ) : undefined;

  return (
    <AuctionDetailDrawerLayout
      slots={{
        image: hasImages ? <DrawerImageDisplay photoUrls={drawerData.photoUrls} title={drawerData.title} /> : undefined,
        title: <DrawerTitle id={titleId}>{drawerData.title}</DrawerTitle>,
        description: drawerData.description ? <RateCardDescription description={drawerData.description} /> : undefined,
        bidBlock: (
          <FormBidBlock
            currentBidAmountInCents={drawerData.currentBidAmountInCents}
            currentBidderName={ticketing.displayBidHistory !== false ? drawerData.topBidderName : undefined}
            hasBids={drawerData.rateHasABid}
            minimumBidLabel={drawerData.minimumBidLabel}
            taxNotice={shouldDisplayAuctionRateTaxMention(rate) ? t("ticketing", "auctionV2.taxNotice") : undefined}
            currency={formCurrency}
            bidMode={bidMode}
            onBidModeChange={handleBidModeChange}
            bidAmountInCents={bidAmountInCents}
            onBidAmountChange={handleBidAmountChange}
            inputError={inputError}
            inputPlaceholder={
              bidMode === "auto"
                ? t("ticketing", "auctionV2.bidBlock.autoBidPlaceholder")
                : hasAlreadyBid
                  ? t("ticketing", "auctionV2.bidBlock.placeholderAlreadyBid")
                  : t("ticketing", "auctionV2.bidBlock.placeholder")
            }
            autoBidDescription={t("ticketing", "auctionV2.bidBlock.autoBidDescription", {
              amount: drawerData.formattedBidIncrement,
            })}
            onSubmit={() => void handleSubmit()}
            submitLabel={
              bidState.kind === "salesScheduled" ? t("ticketing", "openingSoon") : t("ticketing", "auctionV2.bid")
            }
            submitted={submitted}
            disabled={isBiddingBlocked}
            onEditBid={handleEditBid}
            onReturnToList={onClose}
            onGoToCheckout={onGoToCheckout}
            bidBadge={drawerData.bidBadge ? <AuctionBidBadgeChip bidBadge={drawerData.bidBadge} /> : undefined}
            questions={questionsNode}
            isEditing={isEditingBid}
            onCancelBid={handleCancelBid}
            alreadyOutbidHelper={
              isAlreadyOutbidWhileEditing ? t("ticketing", "auctionV2.bidBlock.alreadyOutbidHelper") : undefined
            }
          />
        ),
        countdown: zonedSalesOpenDate ? (
          <FormInfobox
            icon={<Time />}
            description={t("ticketing", "bidsOpeningOn", {
              date: zonedSalesOpenDate.toLocaleString(DateTime.DATE_MED),
              time: zonedSalesOpenDate.toLocaleString(DateTime.TIME_SIMPLE),
            })}
            variant="emphasis"
            data-test="drawer-sales-opening-infobox"
          />
        ) : bidState.kind === "closed" ? (
          <FormInfobox
            icon={<Lock />}
            description={t("ticketing", "auctionV2.antiSniping.rateClosed")}
            data-test="drawer-bidding-closed-infobox"
          />
        ) : countdownLabel ? (
          <FormInfobox icon={<Time />} description={countdownLabel} variant="emphasis" />
        ) : undefined,
        bidHistory:
          ticketing.displayBidHistory === true && drawerData.bidHistory.length > 0 ? (
            <BidHistoryList
              entries={drawerData.bidHistory}
              currency={formCurrency}
              currentCommandIds={bidderCommandIds}
            />
          ) : undefined,
      }}
    />
  );
};
